import React, { useState } from "react";
import LinkHead from "../../../shared/components/LinkHead";
import AuthLayout from "../../../shared/Layouts/AuthLayout";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import ButtonV2 from "../../../shared/components/buttonV2";
import { ENVELOPE, PAD_LOCK } from "../../../assets";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { setStoredEmail } from "../../../states/slices/globalReducer";
import { login } from "../../../Services/auth";
import {
  selectUser,
  setToken,
  setUser,
} from "../../../states/slices/authReducer";
import { toast } from "react-hot-toast";
import { handleNavigate } from "../../../utils";

function SignIn() {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const [formInput, setFormInput] = useState({
    email: "",
    password: "",
  });

  const isValid = formInput.email !== "" && formInput.password !== "";
  const navigate = useNavigate();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;
    const noSpaced = value.replace(/\s/g, "");
    setFormInput((prev) => ({
      ...prev,
      [name]: noSpaced,
    }));
  };
  const handleLogin = async () => {
    dispatch(setStoredEmail(formInput.email));
    setLoading(true);
    try {
      const payload = {
        email: formInput.email,
        password: formInput.password,
      };

      const response = await login(payload);
      if (response) {
        dispatch(setUser(response?.data));
        dispatch(setToken(response?.data?.token));

        setFormInput({
          email: "",
          password: "",
        });

        handleNavigate(response?.data?.type, navigate);
      }
    } catch (error: any) {
      if (error?.response?.status === 428) {
        window.history.pushState({}, "", "/verify-otp");
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
      toast.error(error?.response?.data?.message);
      setLoading(false);
    } finally {
      // setLoading(false);
    }
  };
  return (
    <>
      <LinkHead title="Sign in" content="Login to Lispendes." />

      <AuthLayout>
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="lg:w-[30rem] w-full h-[450px] shadow-boxShadow rounded-lg lg:p-10 p-5 flex flex-col gap-y-6 items-start"
        >
          <div className="flex items-center justify-between w-full">
            {/* Left side */}
            <div className="flex flex-col gap-y-2">
              <span className="text-2xl font-bold text-primary-300">
                Welcome Back
              </span>
              <span className="text-primary-300 font-normal text-sm">
                Don’t have an account?{" "}
                <span
                  className="text-globalGold font-medium cursor-pointer"
                  onClick={() => navigate("/sign-up")}
                >
                  Create an account
                </span>
              </span>
            </div>

            {/* Right side back button */}
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-x-1 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              <span>Back</span>
            </button>
          </div>

          <form action="" className="flex flex-col gap-y-4 w-full">
            <div className="flex flex-col items-start w-full gap-y-1">
              <span className="text-sm font-medium text-[#4F5762]">Email</span>
              <div className="w-full h-[48px] rounded-xl px-3 border border-borderColor shadow-box flex items-center gap-x-2">
                <img src={ENVELOPE} className="w-[20px] h-[20px]" alt="" />
                <input
                  type="text"
                  placeholder="Email"
                  name="email"
                  value={formInput.email}
                  onChange={handleInputChange}
                  className="bg-transparent placeholder:text-sm  w-full outline-none text-primary-300"
                />
              </div>
            </div>

            <div className="flex flex-col items-start w-full gap-y-1">
              <span className="text-sm font-medium text-[#4F5762]">
                Password
              </span>
              <div className="w-full h-[48px] justify-between rounded-xl px-3 border border-borderColor shadow-box flex items-center gap-x-2">
                <div className="flex items-center gap-x-2">
                  <img src={PAD_LOCK} className="w-[20px] h-[20px]" alt="" />

                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formInput.password}
                    onChange={handleInputChange}
                    placeholder="******************"
                    className="bg-transparent placeholder:text-sm  w-full outline-none text-primary-300"
                  />
                </div>
                {showPassword ? (
                  <span
                    className="cursor-pointer "
                    onClick={() => setShowPassword(false)}
                  >
                    <FaEyeSlash color="#808080" />
                  </span>
                ) : (
                  <span
                    className="cursor-pointer"
                    onClick={() => setShowPassword(true)}
                  >
                    <FaEye color="#808080" />
                  </span>
                )}
              </div>
              <span
                onClick={() => navigate("/forgot-password")}
                className="text-globalGold hover:underline cursor-pointer font-semibold text-sm mt-3 flex self-end"
              >
                Forgot Password?
              </span>
            </div>

            <ButtonV2
              title={"Login"}
              loading={loading}
              disabled={loading || !isValid}
              loaderColor="#ffffff"
              btnStyle={
                "bg-globalGold rounded-lg w-full mt-5 h-[45px] flex items-center justify-center"
              }
              textStyle={"text-white text-[16px] text-semibold"}
              handleClick={handleLogin}
            />
          </form>
        </motion.div>
      </AuthLayout>
    </>
  );
}

export default SignIn;
